import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
    useGetStrategiesQuery, 
    useUpdateStrategyMutation,
    type InvestmentStrategy,
    type UpdateInvestmentStrategyPayload 
} from "../api";
const InvestmentStrategiesPage: React.FC = () => {
    const { t } = useTranslation(["common"]);
    const { data, isLoading, refetch } = useGetStrategiesQuery();
    const [updateStrategy, { isLoading: isUpdating }] = useUpdateStrategyMutation();
    
    const [editingStrategy, setEditingStrategy] = useState<InvestmentStrategy | null>(null);
    const [activeLocale, setActiveLocale] = useState<string>("ru");
    const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
    const [formData, setFormData] = useState<UpdateInvestmentStrategyPayload>({
        translations: {}
    });

    const locales = ["ru", "en", "uk", "ka"];

    useEffect(() => {
        if (status?.type === "success") {
            const timer = setTimeout(() => setStatus(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const handleEdit = (strategy: InvestmentStrategy) => {
        setEditingStrategy(strategy);
        
        const translations: Record<string, { name: string; description?: string }> = {};
        locales.forEach(locale => {
            const trans = strategy.translations.find(tr => tr.locale === locale);
            translations[locale] = {
                name: trans?.name || "",
                description: trans?.description || ""
            };
        });

        setFormData({
            color: strategy.color,
            image: strategy.image,
            is_active: strategy.is_active,
            sort_order: strategy.sort_order,
            translations
        });
    };

    const handleSave = async () => {
        if (!editingStrategy) return;

        try {
            await updateStrategy({
                id: editingStrategy.id,
                payload: formData
            }).unwrap();
            setStatus({ type: "success", message: "Стратегия успешно обновлена" });
            setEditingStrategy(null);
            refetch();
        } catch (e: any) {
            setStatus({ type: "error", message: e?.data?.message || "Ошибка при сохранении" });
        }
    };

    if (isLoading) return <div className="p-8 text-center">Загрузка...</div>;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <header className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Стратегии инвестирования</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Управление названиями, описаниями и внешним видом стратегий</p>
                </div>
            </header>

            {status && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center justify-between animate-fade-in ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                    <div className="flex items-center gap-3">
                        <span className="text-xl">{status.type === 'success' ? '✅' : '❌'}</span>
                        <span className="font-medium">{status.message}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="text-current opacity-50 hover:opacity-100 transition-opacity">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data?.data.map((strategy) => (
                    <div 
                        key={strategy.id} 
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                    >
                        <div 
                            className="h-24 flex items-center justify-center relative"
                            style={{ backgroundColor: strategy.color + '20' }}
                        >
                            <div 
                                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-sm"
                                style={{ backgroundColor: strategy.color }}
                            >
                                {strategy.id}
                            </div>
                            <div className="absolute top-4 right-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${strategy.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                    {strategy.is_active ? 'Активна' : 'Неактивна'}
                                </span>
                            </div>
                        </div>
                        
                        <div className="p-5">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{strategy.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 h-10">
                                {strategy.description || <span className="italic">Нет описания</span>}
                            </p>
                            
                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-xs text-gray-400">Порядок: {strategy.sort_order}</span>
                                <button 
                                    onClick={() => handleEdit(strategy)}
                                    className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 rounded-xl text-sm font-medium transition-colors"
                                >
                                    Редактировать
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            {editingStrategy && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Редактирование стратегии #{editingStrategy.id}
                            </h2>
                            <button 
                                onClick={() => setEditingStrategy(null)}
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Цвет (Hex)</label>
                                    <div className="flex gap-2">
                                        <input 
                                            type="color" 
                                            value={formData.color}
                                            onChange={e => setFormData({...formData, color: e.target.value})}
                                            className="w-10 h-10 rounded-lg cursor-pointer border-none p-0 overflow-hidden"
                                        />
                                        <input 
                                            type="text" 
                                            value={formData.color}
                                            onChange={e => setFormData({...formData, color: e.target.value})}
                                            className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Порядок сортировки</label>
                                    <input 
                                        type="number" 
                                        value={formData.sort_order}
                                        onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Изображение</label>
                                    <input 
                                        type="text" 
                                        value={formData.image}
                                        onChange={e => setFormData({...formData, image: e.target.value})}
                                        className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="strategy-0.png"
                                    />
                                </div>
                                <div className="flex items-end pb-2">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative">
                                            <input 
                                                type="checkbox" 
                                                checked={formData.is_active}
                                                onChange={e => setFormData({...formData, is_active: e.target.checked})}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Активна</span>
                                    </label>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex border-b border-gray-100 dark:border-gray-700 mb-6 overflow-x-auto">
                                    {locales.map(locale => (
                                        <button
                                            key={locale}
                                            onClick={() => setActiveLocale(locale)}
                                            className={`px-6 py-3 text-sm font-bold transition-all border-b-2 whitespace-nowrap ${activeLocale === locale ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                                        >
                                            {locale.toUpperCase()}
                                        </button>
                                    ))}
                                </div>

                                {locales.map(locale => (
                                    <div key={locale} className={activeLocale === locale ? 'block animate-fade-in' : 'hidden'}>
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Название ({locale.toUpperCase()})</label>
                                            <input 
                                                type="text" 
                                                value={formData.translations[locale]?.name || ""}
                                                onChange={e => {
                                                    const trans = { ...formData.translations };
                                                    trans[locale] = { ...trans[locale], name: e.target.value };
                                                    setFormData({ ...formData, translations: trans });
                                                }}
                                                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Описание ({locale.toUpperCase()})</label>
                                            <textarea 
                                                rows={4}
                                                value={formData.translations[locale]?.description || ""}
                                                onChange={e => {
                                                    const trans = { ...formData.translations };
                                                    trans[locale] = { ...trans[locale], description: e.target.value };
                                                    setFormData({ ...formData, translations: trans });
                                                }}
                                                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-100 dark:border-gray-700 flex gap-4">
                            <button 
                                onClick={handleSave}
                                disabled={isUpdating}
                                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-2xl font-bold shadow-lg shadow-blue-200 dark:shadow-none transition-all transform active:scale-[0.98]"
                            >
                                {isUpdating ? 'Сохранение...' : 'Сохранить изменения'}
                            </button>
                            <button 
                                onClick={() => setEditingStrategy(null)}
                                className="px-8 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-2xl font-bold transition-all"
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvestmentStrategiesPage;
